/**
 * Saved chart themes (built-ins + team + personal), persisted through `/api/themes` in the org Durable
 * Object. Every theme is registered with ECharts on load and on save, so `echarts.init(el, id)` and
 * `chart.setTheme(id)` always find it.
 */
import { createStore } from "zustand/vanilla";
import { useStore } from "zustand";
import * as echarts from "echarts/core";
import { BUILTIN_THEMES, UI_CHART_THEME, mergeThemes, resolveThemeConflicts, slugify, toEChartsTheme, validateTheme, type ChartTheme, type ThemeMerge } from "./themes";

export interface ThemeState {
  themes: ChartTheme[];
  teamDefault: string;
  selectedId: string;
  draft: ChartTheme | null;          // the editor's working copy
  base: ChartTheme | null;           // the saved version the draft started from (null for a new theme)
  conflict: { merge: ThemeMerge; theirs: ChartTheme; by: string } | null;   // someone saved in between (screenshots/90)
  load: (saved: ChartTheme[], teamDefault?: string) => void;
  select: (id: string) => void;
  edit: (fn: (t: ChartTheme) => ChartTheme) => void;
  createFrom: (baseId: string, name: string, scope: ChartTheme["scope"]) => string;
  save: () => Promise<SaveOutcome>;
  resolve: (keep: Record<string, "mine" | "theirs">) => Promise<SaveOutcome>;
  setTeamDefault: (id: string) => void;
  remove: (id: string) => void;
}

export type SaveOutcome = { ok: boolean; problems: string[]; merged?: string[] };
export type ThemePut = (t: ChartTheme, baseUpdatedAt: string | undefined) => Promise<{ ok: true; value: ChartTheme } | { ok: false; conflict: ChartTheme }>;

export const registerChartTheme = (t: ChartTheme) => {
  const ui = t.id === UI_CHART_THEME ? uiChartTheme(t) : null;
  const e = toEChartsTheme(ui ?? t);
  // Tooltips on the theme's cards (Graphite is dark).
  if (ui && typeof document !== "undefined") e.tooltip = { backgroundColor: cssColor("--bg-surface", "#FFFFFF"), borderColor: ui.grid, textStyle: { color: ui.text } };
  echarts.registerTheme(t.id, e as Parameters<typeof echarts.registerTheme>[1]);
};
const cssColor = (n: string, d: string) => getComputedStyle(document.documentElement).getPropertyValue(n).trim() || d;

/** The default chart theme in the dashboard theme's colors (its CSS variables; the built-in values without a document). */
function uiChartTheme(t: ChartTheme): ChartTheme {
  if (typeof document === "undefined") return t;
  const cs = getComputedStyle(document.documentElement), v = (n: string, d: string) => cs.getPropertyValue(n).trim() || d;
  return { ...t, palette: [v("--chart-1", t.palette[0]), v("--chart-2", t.palette[1]), v("--chart-3", t.palette[2])],
    sequential: [v("--seq-1", t.sequential[0]), v("--seq-2", t.sequential[1]), v("--seq-3", t.sequential[2]), v("--seq-4", t.sequential[3])],
    text: v("--text-primary", t.text), textMuted: v("--text-muted", t.textMuted), grid: v("--chart-grid", t.grid) };
}

/** `api.put` sends `{theme, baseUpdatedAt}` to PUT /api/themes/:id; a 409 body `{conflict}` comes back as `{ok:false, conflict}`. */
export function createThemeStore(api: { put: ThemePut; del: (id: string) => Promise<void>; setDefault: (id: string) => Promise<void> }) {
  BUILTIN_THEMES.forEach(registerChartTheme);
  return createStore<ThemeState>()((set, get) => {
  const commit = (saved: ChartTheme, merged?: string[]): SaveOutcome => {
    registerChartTheme(saved);
    set({ themes: [...get().themes.filter((t) => t.id !== saved.id), saved], draft: structuredClone(saved), base: saved, conflict: null });
    return { ok: true, problems: [], merged };
  };
  const put = async (d: ChartTheme, merged?: string[], retried = false): Promise<SaveOutcome> => {
    const v = validateTheme(d);
    if (v.issues.length) return { ok: false, problems: v.issues };      // palette failures warn but can still save
    const r = await api.put(d, get().base?.updatedAt);
    if (r.ok) return commit(r.value, merged);
    const m = mergeThemes(get().base ?? r.conflict, d, r.conflict);
    const by = r.conflict.updatedBy ?? "Someone on your team";
    if (m.conflicts.length || retried) {
      set({ conflict: { merge: m, theirs: r.conflict, by } });
      return { ok: false, problems: [`${by} saved this theme while you were editing.`], merged: m.fromTheirs };
    }
    set({ draft: m.merged, base: r.conflict });                       // nothing overlapped: merge silently and save once more
    return put(m.merged, m.fromTheirs, true);
  };
  return {
    themes: [...BUILTIN_THEMES], teamDefault: "civic-light", selectedId: "civic-light", draft: null, base: null, conflict: null,
    load: (saved, teamDefault) => {
      saved.forEach(registerChartTheme);
      set({ themes: [...BUILTIN_THEMES, ...saved.filter((t) => t.scope !== "builtin")], teamDefault: teamDefault ?? get().teamDefault });
    },
    select: (id) => { const t = get().themes.find((x) => x.id === id); const own = t && t.scope !== "builtin"; set({ selectedId: id, draft: own ? structuredClone(t) : null, base: own ? t : null, conflict: null }); },
    edit: (fn) => { const d = get().draft; if (d) set({ draft: fn(d) }); },
    createFrom: (baseId, name, scope) => {
      const base = get().themes.find((t) => t.id === baseId) ?? BUILTIN_THEMES[0];
      let id = slugify(name), n = 2;
      while (get().themes.some((t) => t.id === id)) id = `${slugify(name)}-${n++}`;
      const draft: ChartTheme = { ...structuredClone(base), id, name, scope, basedOn: base.id };
      set({ draft, selectedId: id, base: null, conflict: null });
      return id;
    },
    save: async () => {
      const d = get().draft;
      if (!d) return { ok: false, problems: ["Built-in themes can’t be edited. Make a copy first."] };
      return put(d);
    },
    resolve: async (keep) => {
      const c = get().conflict;
      if (!c) return { ok: false, problems: [] };
      const d = resolveThemeConflicts(c.merge, keep);
      set({ draft: d, base: c.theirs, conflict: null });
      return put(d, c.merge.fromTheirs);
    },
    setTeamDefault: (id) => { set({ teamDefault: id }); void api.setDefault(id); },
    remove: (id) => {
      const t = get().themes.find((x) => x.id === id);
      if (!t || t.scope === "builtin") return;
      set({ themes: get().themes.filter((x) => x.id !== id), selectedId: get().teamDefault === id ? "civic-light" : get().selectedId,
        teamDefault: get().teamDefault === id ? "civic-light" : get().teamDefault });
      void api.del(id);
    },
  };
  });
}

export type ThemeStore = ReturnType<typeof createThemeStore>;
export const useThemes = <T,>(store: ThemeStore, sel: (s: ThemeState) => T) => useStore(store, sel);
